import type { GridFilterModel, GridPaginationModel, GridSortModel } from '@mui/x-data-grid';
import { apiGet, apiPost, apiPut, apiPatch, apiDelete, apiPostFormData } from '../services/api';

export type OrderStatus = 'CREATED' | 'BOOKED' | 'CANCELED' | 'COMPLETED';
type PaymentMethod = 'CASH' | 'INSTALLMENTS' | 'CARD' | 'BANK_TRANSFER';

export interface User {
  id: number;
  name: string;
  email: string;
  branch_id: number;
}

export type InstallmentStatus = 'PAID' | 'DUE';
export type PaymentMethodType = 'CASH' | 'CARD' | 'BANK_TRANSFER' | 'CHEQUE';

export interface Installment {
  id: number;
  order_id: number;
  installment_number?: number; // Installment number in the sequence
  due_date: string;
  amount: string | number | null;
  payment_method: PaymentMethodType;
  status: InstallmentStatus;
  created_at: string;
  updated_at: string;
  seq?: number; // Optional for backward compatibility
  paid?: boolean; // Optional for backward compatibility (derived from status)
  notes?: string; // Optional
  order?: Order; // Optional nested order with customer
  employee_name?: string; // Employee name associated with the order
  media?: Array<{ url: string }>; // Optional receipt media
}

export interface Payment {
  id: number;
  order_id: number;
  order_installment_id: number | null;
  amount: string | number | null;
  method: PaymentMethodType;
  paid_at: string;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface Customer {
  id: number;
  name: string;
  mobile_number: string;
}

export interface Order {
  id: number;
  customerName: string;
  customerNumber: string;
  user_id: number;
  branch_id: number;
  people_number: number;
  destination: string;
  // cost, full_amount and their derived amounts are optional on an order,
  // so the API returns null for them until they are filled in.
  cost: string | number | null;
  full_amount: string | number | null;
  paid_amount: string | number | null;
  pending_amount: string | number | null;
  status: OrderStatus;
  notes: string | null;
  confirmed_at: string | null;
  created_at: string;
  updated_at: string;
  user?: User;
  customer?: Customer;
  installments?: Installment[];
  payments?: Payment[];
  media?: Array<{ url: string }>;
}

export async function getMany({
  paginationModel,
  filterModel,
  sortModel,
  userId,
  customerId,
  status,
  sortBy,
  sortOrder,
  dateFrom,
  dateTo,
  customerName,
  mobileNumber,
}: {
  paginationModel: GridPaginationModel;
  sortModel: GridSortModel;
  filterModel: GridFilterModel;
  userId?: number;
  customerId?: number;
  status?: OrderStatus;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  dateFrom?: string;
  dateTo?: string;
  customerName?: string;
  mobileNumber?: string;
}): Promise<{ items: Order[]; itemCount: number }> {
  // Fetch data from REST API with pagination
  const page = paginationModel.page + 1; // API uses 1-based pagination
  const perPage = paginationModel.pageSize;

  // Build query parameters
  const params = new URLSearchParams();
  params.append('page', String(page));
  params.append('per_page', String(perPage));

  if (userId !== undefined) {
    params.append('user_id', String(userId));
  }
  if (customerId !== undefined) {
    params.append('customer_id', String(customerId));
  }
  if (status) {
    params.append('status', status);
  }
  // Always include sort parameters - use defaults if not provided
  params.append('sort_by', sortBy || 'updated_at');
  params.append('sort_order', sortOrder || 'desc');
  if (dateFrom) {
    params.append('date_from', dateFrom);
  }
  if (dateTo) {
    params.append('date_to', dateTo);
  }
  if (mobileNumber) {
    params.append('mobile_number', mobileNumber);
  }
  if (customerName) {
    params.append('name', customerName);
  }

  const response = await apiGet<{
    current_page: number;
    data: Order[];
    total: number;
    per_page: number;
    last_page: number;
  }>(`/api/orders?${params.toString()}`);

  const orders = response.data || [];
  const totalCount = response.total || 0;

  let filteredOrders = [...orders];

  // Apply filters
  if (filterModel?.items?.length) {
    filterModel.items.forEach(({ field, value, operator }) => {
      if (!field || value == null) {
        return;
      }

      filteredOrders = filteredOrders.filter((order) => {
        const orderValue = order[field as keyof Order];

        switch (operator) {
          case 'contains':
            return String(orderValue).toLowerCase().includes(String(value).toLowerCase());
          case 'equals':
            return orderValue === value;
          case 'startsWith':
            return String(orderValue).toLowerCase().startsWith(String(value).toLowerCase());
          case 'endsWith':
            return String(orderValue).toLowerCase().endsWith(String(value).toLowerCase());
          case '>':
            return orderValue > value;
          case '<':
            return orderValue < value;
          default:
            return true;
        }
      });
    });
  }

  // Apply sorting
  if (sortModel?.length) {
    filteredOrders.sort((a, b) => {
      for (const { field, sort } of sortModel) {
        if (a[field as keyof Order] < b[field as keyof Order]) {
          return sort === 'asc' ? -1 : 1;
        }
        if (a[field as keyof Order] > b[field as keyof Order]) {
          return sort === 'asc' ? 1 : -1;
        }
      }
      return 0;
    });
  }

  // Note: Pagination is handled by the API, so we return the data as-is
  // Filtering and sorting can be done client-side if needed, or passed as query params to API

  return {
    items: filteredOrders,
    itemCount: totalCount,
  };
}

export async function getOne(orderId: number) {
  return apiGet<Order>(`/api/orders/${orderId}`);
}

export async function createOne(data: Partial<Omit<Order, 'id' | 'created_at' | 'updated_at' | 'confirmed_at' | 'user' | 'installments' | 'payments'>>) {
  return apiPost<Order>('/api/orders', data);
}

export async function updateOne(orderId: number, data: Partial<Omit<Order, 'id' | 'created_at' | 'updated_at' | 'confirmed_at' | 'user' | 'installments' | 'payments'>>) {
  return apiPut<Order>(`/api/orders/${orderId}`, data);
}

export async function deleteOne(orderId: number) {
  return apiDelete<void>(`/api/orders/${orderId}`);
}

export async function confirmOrder(orderId: number) {
  return apiPost<Order>(`/api/orders/${orderId}/confirm`, {});
}

export interface CancelOrderPayload {
  notes: string;
}

export async function cancelOrder(orderId: number, payload: CancelOrderPayload) {
  return apiPost<Order>(`/api/orders/${orderId}/cancel`, payload);
}

export async function getInstallments(orderId: number): Promise<Installment[]> {
  const response = await apiGet<Installment[] | { data: Installment[] }>(`/api/orders/${orderId}/installments`);
  // Handle both array response and object with data property
  if (Array.isArray(response)) {
    return response;
  }
  return (response as { data: Installment[] }).data || [];
}

export async function setInstallments(orderId: number, installments: Installment[]) {
  return apiPost<void>(`/api/orders/${orderId}/installments`, { installments });
}

export interface OnePaymentPayload {
  type: 'one_payment';
  amount: number;
  payment_method: PaymentMethodType;
}

export interface InstallmentPaymentItem {
  due_date: string;
  amount: number;
  paid?: boolean;
  payment_method?: PaymentMethodType;
}

export interface InstallmentsPaymentPayload {
  type: 'installments';
  installments: InstallmentPaymentItem[];
}

export type PaymentPayload = OnePaymentPayload | InstallmentsPaymentPayload;

export async function payOrder(orderId: number, payload: PaymentPayload) {
  return apiPost<Order>(`/api/orders/${orderId}/pay`, payload);
}

export async function uploadPaymentReceipt(
  orderId: number,
  image: File,
): Promise<{ media: Array<{ url: string }> }> {
  const formData = new FormData();
  formData.append('resource_type', 'order');
  formData.append('resource_id', orderId.toString());
  formData.append('collection', 'receipt');
  formData.append('files[]', image);

  const response = await apiPostFormData<{ media: Array<{ url: string }> }>(
    `/api/upload`,
    formData,
  );

  return response;
}

export interface PayInstallmentPayload {
  method: PaymentMethodType;
  notes?: string;
}

export async function payInstallment(installmentId: number, payload: PayInstallmentPayload) {
  return apiPost<any>(`/api/installments/${installmentId}/pay`, payload);
}

export async function uploadInstallmentReceipt(
  installmentId: number,
  image: File,
): Promise<{ media: Array<{ url: string }> }> {
  const formData = new FormData();
  formData.append('resource_type', 'installment');
  formData.append('resource_id', installmentId.toString());
  formData.append('collection', 'receipt');
  formData.append('files[]', image);

  const response = await apiPostFormData<{ media: Array<{ url: string }> }>(
    `/api/upload`,
    formData,
  );

  return response;
}

export interface UpdateInstallmentPayload {
  due_date?: string; // date (YYYY-MM-DD)
  amount?: number; // numeric, min: 0
  payment_method?: PaymentMethodType; // enum: CASH|CHEQUE|BANK_TRANSFER|CARD
}

export async function updateInstallment(
  installmentId: number,
  payload: UpdateInstallmentPayload,
): Promise<Installment> {
  // Use PUT as specified in requirements, but PATCH is also acceptable
  return apiPut<Installment>(`/api/installments/${installmentId}`, payload);
}

export async function deleteInstallment(installmentId: number): Promise<void> {
  return apiDelete<void>(`/api/installments/${installmentId}`);
}

export interface CreateInstallmentPayload {
  due_date: string; // date (YYYY-MM-DD)
  amount: number; // numeric, min: 0
  payment_method?: PaymentMethodType; // enum: CASH|CHEQUE|BANK_TRANSFER|CARD
}

export interface CreateInstallmentsPayload {
  installments: CreateInstallmentPayload[];
}

export async function createInstallment(
  orderId: number,
  installment: CreateInstallmentPayload,
): Promise<Installment[]> {
  const payload: CreateInstallmentsPayload = {
    installments: [installment],
  };
  return apiPost<Installment[]>(`/api/orders/${orderId}/installments`, payload);
}

export async function getAllInstallments(params?: {
  customer_id?: number;
  user_id?: number;
  order_id?: number;
  status?: 'DUE' | 'PAID';
  due_before?: string;
  due_after?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}): Promise<Installment[]> {
  const queryParams = new URLSearchParams();
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });
  }
  const queryString = queryParams.toString();
  return apiGet<Installment[]>(`/api/installments${queryString ? `?${queryString}` : ''}`);
}

export async function getInstallmentsWithPagination({
  paginationModel,
  userId,
  status,
  sortBy,
  sortOrder,
  dateFrom,
  dateTo,
}: {
  paginationModel: GridPaginationModel;
  userId?: number;
  status?: 'DUE' | 'PAID';
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  dateFrom?: string;
  dateTo?: string;
}): Promise<{ items: Installment[]; itemCount: number; totalAmount?: number }> {
  const page = paginationModel.page + 1; // API uses 1-based pagination
  const perPage = paginationModel.pageSize;

  const params = new URLSearchParams();
  params.append('page', String(page));
  params.append('per_page', String(perPage));

  if (userId !== undefined) {
    params.append('user_id', String(userId));
  }
  if (status) {
    params.append('status', status);
  }
  if (sortBy) {
    params.append('sort_by', sortBy);
  }
  if (sortOrder) {
    params.append('sort_order', sortOrder);
  }
  if (dateFrom) {
    params.append('date_from', dateFrom);
  }
  if (dateTo) {
    params.append('date_to', dateTo);
  }

  const response = await apiGet<{
    current_page: number;
    data: Installment[];
    total: number;
    per_page: number;
    last_page: number;
    total_amount?: number;
  }>(`/api/installments?${params.toString()}`);

  return {
    items: response.data || [],
    itemCount: response.total || 0,
    totalAmount: response.total_amount,
  };
}

// Validation follows the [Standard Schema](https://standardschema.dev/).

type ValidationResult = { issues: { message: string; path: (keyof Order)[] }[] };

export function validate(order: Partial<Order>): ValidationResult {
  let issues: ValidationResult['issues'] = [];

  if (!order.customerName || order.customerName.trim() === '') {
    issues = [...issues, { message: 'validation.customerNameRequired', path: ['customerName'] }];
  }

  if (!order.customerNumber || order.customerNumber.trim() === '') {
    issues = [...issues, { message: 'validation.customerNumberRequired', path: ['customerNumber'] }];
  } else {
    // Validate phone number format (10-15 digits, may include +, spaces, hyphens, parentheses)
    const phoneRegex = /^[\d\s\-+()]{10,20}$/;
    const digitsOnly = order.customerNumber.replace(/[\s\-+()]/g, '');
    if (!phoneRegex.test(order.customerNumber) || digitsOnly.length < 10 || digitsOnly.length > 15) {
      issues = [...issues, { message: 'validation.invalidPhoneNumber', path: ['customerNumber'] }];
    }
  }

  if (!order.destination || order.destination.trim() === '') {
    issues = [...issues, { message: 'validation.destinationRequired', path: ['destination'] }];
  }

  if (!order.people_number || order.people_number <= 0) {
    issues = [...issues, { message: 'validation.seatsGreaterThanZero', path: ['people_number'] }];
  }

  // cost, price_per_person and full_amount are optional; only validate them
  // (must be greater than 0) when a value has actually been provided.
  const orderAny = order as any;

  const pricePerPerson = orderAny.price_per_person;
  if (pricePerPerson !== undefined && pricePerPerson !== null && pricePerPerson !== '' && Number(pricePerPerson) <= 0) {
    issues = [...issues, { message: 'validation.pricePerPersonMustBePositive', path: ['price_per_person' as keyof Order] }];
  }

  const totalAmount = orderAny.full_amount;
  if (totalAmount !== undefined && totalAmount !== null && totalAmount !== '' && Number(totalAmount) <= 0) {
    issues = [...issues, { message: 'validation.totalAmountMustBePositive', path: ['full_amount' as keyof Order] }];
  }

  const cost = orderAny.cost;
  if (cost !== undefined && cost !== null && cost !== '' && Number(cost) <= 0) {
    issues = [...issues, { message: 'validation.costMustBePositive', path: ['cost' as keyof Order] }];
  }
  return { issues };
}

export interface OrdersStatistics {
  counts: {
    CREATED: number;
    COMPLETED: number;
    BOOKED: number;
    CANCELED: number;
  };
  total: number;
}

export async function getAllOrdersStatistics(
  dateFrom?: string | null,
  dateTo?: string | null
): Promise<OrdersStatistics> {
  const params = new URLSearchParams();

  if (dateFrom) {
    params.append('date_from', dateFrom);
  }

  if (dateTo) {
    params.append('date_to', dateTo);
  }

  const queryString = params.toString();
  return apiGet<OrdersStatistics>(`/api/orders-statistics/all${queryString ? `?${queryString}` : ''}`);
}

